/**
 * PhotoCarousel — FE-212.
 *
 * Horizontally-scrollable carousel for an item's photos. Replaces
 * the static hero `<Image>` on ItemDetail. Each slide is one
 * PhotoSummary; the parent controls width and tap-to-enlarge.
 *
 * Behavior:
 *   - One slide per item.photos[] entry, in display_order
 *   - Snap-scroll between slides (paging)
 *   - Dot indicator at the bottom; tappable dots jump-scroll
 *   - Tap any photo → fires `onPhotoPress(photo, index)` so the
 *     parent can open the existing ImageEnlargementModal
 *   - When `photos` is empty BUT a `fallbackUrl` is provided
 *     (legacy items pre-BE-201 backfill, or items whose
 *     primary-photo job is still queued), render a single
 *     fallback slide
 *   - Manage-photos affordance is OWNER-only: parent passes
 *     `onManagePress` (undefined hides the button)
 */

import React, { useRef, useState, useCallback } from 'react';
import {
  Image,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
  useWindowDimensions,
  NativeSyntheticEvent,
  NativeScrollEvent,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { fpColors } from '../../../styles/theme';
import { isHttpUrl } from '../../../utils/imageUri';
import type { PhotoSummary } from '../../../api/types';

export interface PhotoCarouselProps {
  /** Multi-photo list, already ordered by display_order. Empty
   *  arrays fall back to `fallbackUrl` (or render a placeholder). */
  photos: PhotoSummary[];
  /** Hero photo URL used ONLY when `photos` is empty — covers
   *  legacy items pre-BE-201 backfill and the in-flight state
   *  while the primary-photo job is queued. */
  fallbackUrl?: string | null;
  /** Original scan image URL — the full room photo where this item
   *  was detected. Surfaced as a final carousel slide with an
   *  "Original scan" label badge so users can swipe to see the
   *  source context (e.g. "I want to see the original — where was
   *  this in my kitchen"). Suppressed when it would duplicate
   *  another slide (legacy items where `fallbackUrl === sourceUrl`
   *  because no PRIMARY_AUTO cutout exists yet). */
  sourceUrl?: string | null;
  /** Tap-to-enlarge handler. Receives the photo + its index in
   *  the carousel so the modal can open at the right offset. */
  onPhotoPress?: (photo: PhotoSummary | { url: string }, index: number) => void;
  /** Owner-only — when set, renders the "Edit photos" affordance
   *  at the top-right of the hero. Hidden for non-owners. */
  onManagePress?: () => void;
  /** Override the carousel height (default = square of screen
   *  width, capped at a reasonable max for desktop). */
  height?: number;
}

export const PhotoCarousel: React.FC<PhotoCarouselProps> = ({
  photos,
  fallbackUrl,
  sourceUrl,
  onPhotoPress,
  onManagePress,
  height,
}) => {
  const { width: screenWidth } = useWindowDimensions();
  // The carousel sits inside the standard ItemDetail content stack,
  // which has 16px horizontal padding and a 12px corner-radius card
  // chrome. Subtract the padding so the slides fill the card width.
  const slideWidth = Math.min(screenWidth, 720);
  const carouselHeight = height ?? Math.min(slideWidth, 420);

  // Build the slide list. When `photos` is non-empty it's the source
  // of truth; otherwise fall through to fallbackUrl as a single
  // synthetic slide (legacy items / queued primary-photo). Keeping
  // both branches under one render path means the dot indicator and
  // tap-to-enlarge logic don't need to special-case the fallback.
  //
  // FE-249.2 — hoist the PRIMARY_AUTO row to the front. Per BE spec
  // it has display_order=0 and is the canonical hero.
  //
  // FE-249.5 — if photos[] has NO PRIMARY_AUTO row (legacy items
  // pre BE-235 backfill — confirmed by BE 2026-05-22: "0 of 10
  // random items had any PRIMARY_AUTO row"), synthesize a primary
  // slide from `fallbackUrl`. That URL is the same one the Items
  // grid uses for the thumbnail (primary_photo_url ||
  // source_image_url || image_url), so the carousel hero stays
  // consistent with the rest of the app — the first slide users
  // see is always the same image they tapped to land here.
  const primaryFromPhotos = photos.find((p) => p.source === 'PRIMARY_AUTO');
  const userUploads = photos.filter((p) => p.source !== 'PRIMARY_AUTO');
  const rawSlides: Array<{
    key: string;
    url: string;
    photo?: PhotoSummary;
    // Tag a slide as the original-scan slide so we can render the
    // "Original scan" label badge on top of it without confusing
    // PhotoSummary's normal display.
    sourceLabel?: 'scan';
  }> = [];
  if (primaryFromPhotos) {
    rawSlides.push({
      key: `photo-${primaryFromPhotos.id}`,
      url: primaryFromPhotos.url,
      photo: primaryFromPhotos,
    });
  } else if (fallbackUrl) {
    // Synthetic primary — no DB row, but visually the hero.
    rawSlides.push({ key: 'photo-fallback-primary', url: fallbackUrl });
  }
  for (const p of userUploads) {
    rawSlides.push({ key: `photo-${p.id}`, url: p.url, photo: p });
  }
  // FE — Source-image slide: append the original scan photo as a
  // final slide when (a) the item has one and (b) it doesn't
  // duplicate the synthetic primary (which already IS source_image_url
  // for legacy items). Wins back the "where was this scanned from?"
  // affordance without making users hunt for the small Source-scan
  // card lower on the page. Users swipe through the same carousel
  // they already understand.
  if (sourceUrl && !rawSlides.some((s) => s.url === sourceUrl)) {
    rawSlides.push({
      key: 'photo-source-scan',
      url: sourceUrl,
      sourceLabel: 'scan',
    });
  }

  // backend#364 defense-in-depth: <Image> can only load http(s) URIs. A
  // freshly-generated cutout can transiently arrive as a raw `gs://…`
  // path (and a few legacy rows are malformed) — rendering those gives a
  // blank slide with no HTTP error. Drop non-http slides so the carousel
  // only shows loadable images and the count/dots stay consistent.
  const slides = rawSlides.filter((s) => isHttpUrl(s.url));

  const [index, setIndex] = useState(0);
  const scrollRef = useRef<ScrollView | null>(null);

  const handleScroll = useCallback(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      const x = e.nativeEvent.contentOffset.x;
      const next = Math.round(x / slideWidth);
      if (next !== index && next >= 0 && next < slides.length) {
        setIndex(next);
      }
    },
    [index, slideWidth, slides.length],
  );

  const jumpTo = useCallback(
    (i: number) => {
      if (!scrollRef.current) return;
      scrollRef.current.scrollTo({ x: i * slideWidth, animated: true });
      setIndex(i);
    },
    [slideWidth],
  );

  // Empty state — no photos AND no fallback. Render a placeholder
  // card so the layout doesn't collapse. Owner sees an "+ Add photo"
  // CTA; non-owner sees a muted placeholder.
  //
  // FE #212 — cap empty-state height at 140px instead of the regular
  // carousel height (~420px). Previously the placeholder dominated
  // ~70% of an iPhone 13 viewport, pushing custody + bento + history
  // below the fold. Compact placeholder keeps the rest of the page
  // immediately visible. (Once #200 / BE #278 ship and items always
  // have a synthesized or real primary photo, this empty branch is
  // a rare edge case anyway.)
  if (slides.length === 0) {
    return (
      <View
        style={[styles.placeholderWrap, { height: 140 }]}
        testID="photo-carousel-empty"
      >
        <MaterialIcons name="image" size={32} color={fpColors.outline} />
        <Text style={styles.placeholderText}>No photos yet</Text>
        {onManagePress ? (
          <Pressable
            onPress={onManagePress}
            style={({ pressed }) => [
              styles.placeholderCta,
              pressed && { opacity: 0.85 },
            ]}
            accessibilityRole="button"
            accessibilityLabel="Add the first photo"
            testID="photo-carousel-add-first"
          >
            <MaterialIcons
              name="add-a-photo"
              size={16}
              color={fpColors.onPrimary}
            />
            <Text style={styles.placeholderCtaText}>Add a photo</Text>
          </Pressable>
        ) : null}
      </View>
    );
  }

  return (
    <View style={styles.wrap} testID="photo-carousel">
      <ScrollView
        ref={scrollRef}
        horizontal
        pagingEnabled
        showsHorizontalScrollIndicator={false}
        onScroll={handleScroll}
        scrollEventThrottle={16}
        style={{ height: carouselHeight }}
        contentContainerStyle={{ height: carouselHeight }}
        accessibilityRole="adjustable"
        accessibilityLabel={`Photo ${index + 1} of ${slides.length}`}
      >
        {slides.map((s, i) => (
          <Pressable
            key={s.key}
            onPress={() => onPhotoPress?.(s.photo ?? { url: s.url }, i)}
            style={({ pressed }) => [
              styles.slide,
              { width: slideWidth, height: carouselHeight },
              pressed && { opacity: 0.95 },
            ]}
            accessibilityRole="imagebutton"
            accessibilityLabel={`Tap to enlarge photo ${i + 1}`}
            testID={`photo-carousel-slide-${i}`}
          >
            <Image
              source={{ uri: s.url }}
              style={[
                styles.image,
                { width: slideWidth, height: carouselHeight },
                // "Fit item to view" — the PRIMARY_AUTO cutout is an
                // isolated item on a neutral bg, so `contain` shows the
                // WHOLE item (centered, never cropped). User uploads + the
                // original scan are full photos → keep `cover` so they
                // fill the frame edge-to-edge.
                s.photo?.source === 'PRIMARY_AUTO' && {
                  backgroundColor: fpColors.surfaceContainerLowest,
                },
              ]}
              resizeMode={s.photo?.source === 'PRIMARY_AUTO' ? 'contain' : 'cover'}
              accessibilityIgnoresInvertColors
            />
            {/* "Magic crop" badge on slide 0 when it's the PRIMARY_AUTO
                cutout — tells users this photo was automatically cropped
                out of their scan by AI (the sparkle reinforces "magic"). */}
            {i === 0 && s.photo?.source === 'PRIMARY_AUTO' ? (
              <View style={styles.primaryBadge}>
                <MaterialIcons
                  name="auto-awesome"
                  size={10}
                  color={fpColors.onPrimary}
                />
                <Text style={styles.primaryBadgeText}>Magic crop</Text>
              </View>
            ) : null}
            {/* "Original scan" badge on the source-image slide so
                users immediately understand the photo's role
                (it's the room context, not another cutout). */}
            {s.sourceLabel === 'scan' ? (
              <View style={styles.sourceBadge}>
                <MaterialIcons
                  name="photo-library"
                  size={11}
                  color={fpColors.onPrimary}
                />
                <Text style={styles.primaryBadgeText}>Original scan</Text>
              </View>
            ) : null}
          </Pressable>
        ))}
      </ScrollView>

      {/* FE-237 — Tap-edges navigation on web. Native handles swipe
          fine, but desktop visitors expect clicking the left/right
          edge of the photo to advance the carousel. Two invisible
          overlay pressables on the left+right 22% of the carousel
          width. Disabled on the only slide (no point), and on the
          slides where the direction would wrap around (we don't loop,
          per existing behavior).
          Native (iOS/Android): skipped so the existing swipe
          gestures aren't intercepted by an overlay. */}
      {Platform.OS === 'web' && slides.length > 1 ? (
        <>
          {index > 0 ? (
            <Pressable
              onPress={() => jumpTo(index - 1)}
              accessibilityRole="button"
              accessibilityLabel="Previous photo"
              style={[styles.edgeNav, styles.edgeNavLeft, { height: carouselHeight }]}
              testID="photo-carousel-edge-prev"
            >
              <View style={styles.edgeChevron}>
                <MaterialIcons
                  name="chevron-left"
                  size={28}
                  color={fpColors.onSurface}
                />
              </View>
            </Pressable>
          ) : null}
          {index < slides.length - 1 ? (
            <Pressable
              onPress={() => jumpTo(index + 1)}
              accessibilityRole="button"
              accessibilityLabel="Next photo"
              style={[styles.edgeNav, styles.edgeNavRight, { height: carouselHeight }]}
              testID="photo-carousel-edge-next"
            >
              <View style={styles.edgeChevron}>
                <MaterialIcons
                  name="chevron-right"
                  size={28}
                  color={fpColors.onSurface}
                />
              </View>
            </Pressable>
          ) : null}
        </>
      ) : null}

      {/* Dot indicator — only when there's more than one slide. */}
      {slides.length > 1 ? (
        <View
          style={styles.dotRow}
          accessibilityRole="tablist"
          accessibilityLabel={`Photo ${index + 1} of ${slides.length}`}
        >
          {slides.map((_, i) => (
            <Pressable
              key={`dot-${i}`}
              onPress={() => jumpTo(i)}
              accessibilityRole="tab"
              accessibilityLabel={`Go to photo ${i + 1}`}
              accessibilityState={{ selected: i === index }}
              hitSlop={6}
              testID={`photo-carousel-dot-${i}`}
            >
              <View
                style={[
                  styles.dot,
                  i === index && styles.dotActive,
                ]}
              />
            </Pressable>
          ))}
        </View>
      ) : null}

      {/* Owner-only Edit Photos affordance, pinned top-right. */}
      {onManagePress ? (
        <Pressable
          onPress={onManagePress}
          style={({ pressed }) => [
            styles.editBtn,
            pressed && { opacity: 0.85 },
          ]}
          accessibilityRole="button"
          accessibilityLabel="Edit photos"
          hitSlop={8}
          testID="photo-carousel-manage-btn"
        >
          <MaterialIcons
            name="photo-library"
            size={16}
            color={fpColors.onSurface}
          />
          <Text style={styles.editBtnText}>Edit</Text>
        </Pressable>
      ) : null}

      {/* Count chip bottom-right — only when > 1 photo. Useful
          orientation aid even after the dots scroll out of focus. */}
      {slides.length > 1 ? (
        <View style={styles.countPill}>
          <Text style={styles.countPillText}>
            {index + 1} / {slides.length}
          </Text>
        </View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  wrap: {
    position: 'relative',
    width: '100%',
    backgroundColor: fpColors.surfaceContainerLow,
    borderRadius: 12,
    overflow: 'hidden',
  },
  slide: {
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: fpColors.surfaceContainerLowest,
  },
  image: {
    backgroundColor: fpColors.surfaceContainerLow,
  },
  primaryBadge: {
    position: 'absolute',
    top: 10,
    left: 10,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    paddingHorizontal: 7,
    paddingVertical: 3,
    borderRadius: 10,
    backgroundColor: fpColors.primary,
    shadowColor: '#000',
    shadowOpacity: 0.15,
    shadowOffset: { width: 0, height: 1 },
    shadowRadius: 2,
    elevation: 2,
  },
  primaryBadgeText: {
    fontSize: 10,
    fontWeight: '700',
    color: fpColors.onPrimary,
    letterSpacing: 0.2,
  },
  // Tertiary tint distinguishes the source-scan badge from the
  // PRIMARY_AUTO badge — same shape + size + position, different
  // background so users see "this is the original room photo, not
  // the auto-cutout."
  sourceBadge: {
    position: 'absolute',
    top: 10,
    left: 10,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    paddingHorizontal: 7,
    paddingVertical: 3,
    borderRadius: 10,
    backgroundColor: (fpColors as any).tertiary ?? fpColors.primary,
    shadowColor: '#000',
    shadowOpacity: 0.15,
    shadowOffset: { width: 0, height: 1 },
    shadowRadius: 2,
    elevation: 2,
  },
  dotRow: {
    position: 'absolute',
    bottom: 10,
    alignSelf: 'center',
    flexDirection: 'row',
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 5,
    backgroundColor: 'rgba(0, 0, 0, 0.32)',
    borderRadius: 12,
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: 'rgba(255, 255, 255, 0.5)',
  },
  dotActive: {
    backgroundColor: '#fff',
    width: 16,
  },
  editBtn: {
    position: 'absolute',
    top: 10,
    right: 10,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
    backgroundColor: 'rgba(255, 255, 255, 0.92)',
    shadowColor: '#000',
    shadowOpacity: 0.12,
    shadowOffset: { width: 0, height: 1 },
    shadowRadius: 2,
    elevation: 2,
  },
  editBtnText: {
    fontSize: 12,
    fontWeight: '700',
    color: fpColors.onSurface,
  },
  countPill: {
    position: 'absolute',
    bottom: 10,
    right: 10,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 10,
    backgroundColor: 'rgba(0, 0, 0, 0.45)',
  },
  // FE-237 — invisible edge overlays for web tap-nav. Sized at ~22%
  // of the carousel width so they don't interfere with center-tap to
  // enlarge but DO catch desktop clicks near the photo edges.
  edgeNav: {
    position: 'absolute',
    top: 0,
    width: '22%',
    justifyContent: 'center',
    // Push the chevron in slightly from the edge so it doesn't overlap
    // the dots / count pill at the bottom-corners.
    paddingHorizontal: 8,
  },
  edgeNavLeft: {
    left: 0,
    alignItems: 'flex-start',
  },
  edgeNavRight: {
    right: 0,
    alignItems: 'flex-end',
  },
  edgeChevron: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.78)',
    opacity: 0.7,
  },
  countPillText: {
    fontSize: 11,
    fontWeight: '600',
    color: '#fff',
  },
  placeholderWrap: {
    width: '100%',
    backgroundColor: fpColors.surfaceContainerLow,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    padding: 24,
  },
  placeholderText: {
    fontSize: 14,
    color: fpColors.onSurfaceVariant,
  },
  placeholderCta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 14,
    paddingVertical: 8,
    backgroundColor: fpColors.primary,
    borderRadius: 8,
  },
  placeholderCtaText: {
    fontSize: 13,
    fontWeight: '700',
    color: fpColors.onPrimary,
  },
});
